/**
 * Browser-side similarity aggregation for the work-focus session guard.
 *
 * The template arithmetic that *builds* and *compares* face templates deliberately does not live
 * here — it is in `admin-api/src/domain/faceTemplate.ts`, behind the authenticated enrolment and
 * verification endpoints. Two reasons it must stay server-side: a page that computes its own
 * match verdict can simply report `match` forever, and reaching the embedding service needs an
 * internal secret that must never be shipped to a browser.
 *
 * What remains is what the browser genuinely decides for itself — how to smooth a stream of
 * server-issued similarity scores, and whether a re-check clears a lock. Keeping the split at
 * this line means there is no second copy of the matching arithmetic to drift out of step.
 */

export interface SimilaritySample {
  readonly similarity: number;
  readonly monotonicTs: number;
}

export interface AggregatedSimilarity {
  readonly value: number | null;
  readonly sampleCount: number;
  readonly latest: number | null;
}

/**
 * Combine recent comparisons into one similarity value.
 *
 * **Median, deliberately not the maximum.** Taking the best of the last N was the first
 * implementation and it was wrong in a way that defeated the whole control: the window spans a
 * person swap, so the previous occupant's high scores stay in it and keep reporting `match`
 * after somebody else sits down. Observed in testing as a single frame scoring 0.306 while the
 * aggregate reported 0.652.
 *
 * A median keeps the original benefit — one blurred, half-lit or mid-blink frame cannot cause a
 * false rejection — without letting one stale favourable frame mask a sustained run of low
 * scores. Samples older than `maxAgeSeconds` are dropped, so a verdict always describes the
 * recent past rather than whoever was there earlier.
 */
export function aggregateSimilarity(
  samples: readonly SimilaritySample[],
  options: { now: number; maxAgeSeconds: number },
): AggregatedSimilarity {
  const fresh = samples
    .filter((sample) => options.now - sample.monotonicTs <= options.maxAgeSeconds)
    .filter((sample) => Number.isFinite(sample.similarity));

  if (fresh.length === 0) return { value: null, sampleCount: 0, latest: null };

  const latest = (fresh[fresh.length - 1] as SimilaritySample).similarity;
  const sorted = fresh.map((sample) => sample.similarity).sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 === 1
      ? (sorted[middle] as number)
      : ((sorted[middle - 1] as number) + (sorted[middle] as number)) / 2;

  return { value: median, sampleCount: fresh.length, latest };
}
